import { useEffect } from 'react'
import { useListStore } from 'app/stores/StoreContext'
import { bg } from 'lib/background'

/**
 * The workspace's Lists for the grouped sidebar, read once on mount. Call from an `observer()`
 * component: the store's `hits` are MobX state. `loading` is true until the first read lands,
 * so a caller holds off anything that would act on an empty list. With `enabled: false` it reads
 * nothing, for a surface whose toggle-off path must send no list request.
 */
export function usePracticeLists(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true
  const listStore = useListStore()
  const { list, listLoading, listError } = listStore

  useEffect(() => {
    // A failed read leaves `list` undefined with `listError` set: without that last guard the
    // loading flag falling back to false would re-run this effect and ask again forever.
    if (enabled && list === undefined && !listLoading && !listError) bg(() => listStore.fetchList())
  }, [enabled, listStore, list, listLoading, listError])

  return {
    lists: listStore.hits,
    loading: list === undefined,
    // Writes elsewhere never touch the store and its own read is cache-first.
    refetch: () => listStore.fetchList(undefined, { requestPolicy: `network-only` }),
  }
}
